import { 
  Clock, 
  Zap, 
  Play, 
  Pause, 
  Plus,
  Edit2,
  Settings,
  Activity,
  Timer,
  CheckCircle,
  AlertTriangle,
  FileText,
  Database,
  Webhook,
  GitBranch,
  BarChart3,
  Calendar as CalendarIcon
} from 'lucide-react';
import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Agent } from '../../../../stores/agentStore';

interface AgentSchedulingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent;
}

interface ScheduleRule {
  id: string;
  name: string;
  type: 'cron' | 'interval' | 'event';
  enabled: boolean;
  cronExpression?: string;
  interval?: number;
  intervalUnit?: 'minutes' | 'hours' | 'days';
  eventType?: string;
  conditions?: Condition[];
  action: string;
  instructions: string;
  targetNodes?: string[];
  priority: 'low' | 'medium' | 'high' | 'critical';
  createdAt: string;
  lastRun?: string;
  nextRun?: string;
  runCount: number;
  successRate: number;
  avgExecutionTime: number;
}

interface Condition {
  id: string;
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than' | 'exists' | 'not_exists';
  value: string;
  logicalOperator?: 'AND' | 'OR';
}

interface EventTrigger {
  id: string;
  name: string;
  type: 'file_change' | 'api_call' | 'data_update' | 'webhook' | 'node_create' | 'node_update' | 'ai_request';
  enabled: boolean;
  config: Record<string, any>;
  conditions?: Condition[];
  action: string;
  instructions: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  createdAt: string;
  triggerCount: number;
  lastTriggered?: string;
  successRate: number;
  avgResponseTime: number;
}

interface TriggerChain {
  id: string;
  name: string;
  enabled: boolean;
  stages: {
    agentId: string;
    agentName: string;
    action: string;
    conditions?: Condition[];
    timeout: number;
    retryCount: number;
  }[];
  createdAt: string;
  executionCount: number;
  successRate: number;
  avgExecutionTime: number;
}

const AgentSchedulingDialog: React.FC<AgentSchedulingDialogProps> = ({ isOpen, onClose, agent }) => {
  const [activeTab, setActiveTab] = useState('schedules');
  const [showCreateSchedule, setShowCreateSchedule] = useState(false);

  // Form states
  const [newSchedule, setNewSchedule] = useState({
    name: '',
    type: 'cron' as const,
    cronExpression: '0 9 * * 1-5', // Weekdays at 9 AM
    interval: 60,
    intervalUnit: 'minutes' as const,
    action: '',
    instructions: '',
    priority: 'medium' as const
  });

  const setShowCreateTrigger = () => console.log('Create trigger dialog - not implemented yet');
  const setShowCreateChain = () => console.log('Create chain dialog - not implemented yet');

  // Mock data for demonstration
  const [schedules] = useState<ScheduleRule[]>([
    {
      id: 'sched_001',
      name: 'Daily Security Scan',
      type: 'cron',
      enabled: true,
      cronExpression: '0 2 * * *',
      action: 'security_scan',
      instructions: 'Perform comprehensive security scan of all observed nodes',
      priority: 'high',
      createdAt: '2025-01-10T14:30:00Z',
      lastRun: '2025-01-15T02:00:00Z',
      nextRun: '2025-01-16T02:00:00Z',
      runCount: 47,
      successRate: 95.7,
      avgExecutionTime: 180
    },
    {
      id: 'sched_002',
      name: 'Hourly Performance Check',
      type: 'interval',
      enabled: true,
      interval: 1,
      intervalUnit: 'hours',
      action: 'performance_monitor',
      instructions: 'Monitor performance metrics and alert on anomalies',
      priority: 'medium',
      createdAt: '2025-01-12T09:15:00Z',
      lastRun: '2025-01-15T11:00:00Z',
      nextRun: '2025-01-15T12:00:00Z',
      runCount: 168,
      successRate: 98.2,
      avgExecutionTime: 45
    },
    {
      id: 'sched_003',
      name: 'Weekly Backup Verification',
      type: 'cron',
      enabled: false,
      cronExpression: '0 1 * * 0',
      action: 'backup_verify',
      instructions: 'Verify backup integrity and generate report',
      priority: 'critical',
      createdAt: '2025-01-08T16:45:00Z',
      lastRun: '2025-01-14T01:00:00Z',
      nextRun: '2025-01-21T01:00:00Z',
      runCount: 12,
      successRate: 100,
      avgExecutionTime: 720
    }
  ]);

  const triggers: EventTrigger[] = [
    {
      id: 'trigger_001',
      name: 'Code Repository Changes',
      type: 'file_change',
      enabled: true,
      config: { 
        path: '/src/**/*.ts', 
        events: ['create', 'modify'], 
        debounce: 5000 
      },
      action: 'code_review',
      instructions: 'Analyze code changes for security vulnerabilities and best practices',
      priority: 'high',
      createdAt: '2025-01-05T10:20:00Z',
      triggerCount: 234,
      lastTriggered: '2025-01-15T10:45:00Z',
      successRate: 94.4,
      avgResponseTime: 3200
    },
    {
      id: 'trigger_002',
      name: 'API Error Rate Spike',
      type: 'data_update',
      enabled: true,
      config: { 
        metric: 'api_error_rate', 
        threshold: 5, 
        window: '5m' 
      },
      action: 'incident_response',
      instructions: 'Investigate API errors and notify team if threshold exceeded',
      priority: 'critical',
      createdAt: '2025-01-08T14:10:00Z',
      triggerCount: 8,
      lastTriggered: '2025-01-14T16:22:00Z',
      successRate: 100,
      avgResponseTime: 1200
    },
    {
      id: 'trigger_003',
      name: 'New Node Creation',
      type: 'node_create',
      enabled: true,
      config: { 
        nodeTypes: ['database', 'api'], 
        conditions: { complexity: 'high' } 
      },
      action: 'architecture_review',
      instructions: 'Review new database/API nodes for architectural compliance',
      priority: 'medium',
      createdAt: '2025-01-12T11:30:00Z',
      triggerCount: 15,
      lastTriggered: '2025-01-15T09:15:00Z',
      successRate: 93.3,
      avgResponseTime: 5400
    }
  ];

  const chains: TriggerChain[] = [
    {
      id: 'chain_001',
      name: 'CI/CD Pipeline Review',
      enabled: true,
      stages: [
        { agentId: 'agent_001', agentName: 'Security Analyst', action: 'security_scan', timeout: 300, retryCount: 2 },
        { agentId: 'agent_002', agentName: 'Code Reviewer', action: 'code_review', timeout: 600, retryCount: 3 },
        { agentId: 'agent_003', agentName: 'Performance Tester', action: 'performance_test', timeout: 900, retryCount: 1 }
      ],
      createdAt: '2025-01-10T15:20:00Z',
      executionCount: 42,
      successRate: 88.1,
      avgExecutionTime: 1240
    },
    {
      id: 'chain_002',
      name: 'Incident Response Chain',
      enabled: true,
      stages: [
        { agentId: 'agent_004', agentName: 'Monitor Agent', action: 'assess_severity', timeout: 60, retryCount: 1 },
        { agentId: 'agent_005', agentName: 'Alert Manager', action: 'notify_team', timeout: 120, retryCount: 2 },
        { agentId: 'agent_006', agentName: 'Remediation Agent', action: 'auto_fix', timeout: 1800, retryCount: 3 }
      ],
      createdAt: '2025-01-09T08:45:00Z',
      executionCount: 7,
      successRate: 85.7,
      avgExecutionTime: 890
    }
  ];

  const handleCreateSchedule = () => {
    // In real implementation, this would call the API
    console.log('Creating schedule:', newSchedule);
    setShowCreateSchedule(false);
    setNewSchedule({
      name: '',
      type: 'cron',
      cronExpression: '0 9 * * 1-5',
      interval: 60,
      intervalUnit: 'minutes',
      action: '',
      instructions: '',
      priority: 'medium'
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'low': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (enabled: boolean, successRate: number) => {
    if (!enabled) return <Pause className="w-4 h-4 text-gray-500" />;
    if (successRate >= 95) return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (successRate >= 80) return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
    return <AlertTriangle className="w-4 h-4 text-red-500" />;
  };

  const formatNextRun = (nextRun?: string) => {
    if (!nextRun) return 'Not scheduled';
    const date = new Date(nextRun);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    
    if (diff < 0) return 'Overdue';
    if (diff < 60000) return 'In less than 1 minute';
    if (diff < 3600000) return `In ${Math.floor(diff / 60000)} minutes`;
    if (diff < 86400000) return `In ${Math.floor(diff / 3600000)} hours`;
    return `In ${Math.floor(diff / 86400000)} days`;
  };

  const getTriggerTypeIcon = (type: string) => {
    switch (type) {
      case 'file_change': return <FileText className="w-4 h-4" />;
      case 'api_call': return <Webhook className="w-4 h-4" />;
      case 'data_update': return <Database className="w-4 h-4" />;
      case 'webhook': return <Webhook className="w-4 h-4" />;
      case 'node_create': return <Plus className="w-4 h-4" />;
      case 'node_update': return <Edit2 className="w-4 h-4" />;
      case 'ai_request': return <Zap className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Advanced Scheduling & Triggers - {agent.name}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="schedules" className="flex items-center gap-2">
              <Timer className="w-4 h-4" />
              Schedules
            </TabsTrigger>
            <TabsTrigger value="triggers" className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Event Triggers
            </TabsTrigger>
            <TabsTrigger value="chains" className="flex items-center gap-2">
              <GitBranch className="w-4 h-4" />
              Trigger Chains
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="schedules" className="flex-1 space-y-4 min-h-0">
            <div className="flex justify-between items-center">
              <div className="space-y-1">
                <h3 className="text-lg font-semibold">Scheduled Tasks</h3>
                <p className="text-sm text-muted-foreground">Manage time-based and interval-based agent automation</p>
              </div>
              <Button onClick={() => setShowCreateSchedule(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Schedule
              </Button>
            </div>

            <ScrollArea className="flex-1 min-h-0">
              <div className="space-y-4">
                {schedules.map((schedule) => (
                  <Card key={schedule.id}>
                    <CardHeader className="" hasCardAction={false}>
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <CardTitle className="text-base">{schedule.name}</CardTitle>
                            {getStatusIcon(schedule.enabled, schedule.successRate)}
                            <Badge variant="outline" className={getPriorityColor(schedule.priority)}>
                              {schedule.priority}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="w-3 h-3" />
                              {schedule.type === 'cron' ? schedule.cronExpression : `Every ${schedule.interval} ${schedule.intervalUnit}`}
                            </span>
                            <span>•</span>
                            <span>Next: {formatNextRun(schedule.nextRun)}</span>
                            <span>•</span>
                            <span>{schedule.runCount} executions</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline">
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" disabled={!schedule.enabled}>
                            <Play className="w-3 h-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {/* Toggle enabled state */}}
                          >
                            {schedule.enabled ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <h4 className="text-sm font-medium mb-1">Instructions</h4>
                        <p className="text-sm text-muted-foreground">{schedule.instructions}</p>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Success Rate</span>
                          <div className="flex items-center gap-2 mt-1">
                            <Progress value={schedule.successRate} className="h-2 flex-1" />
                            <span className="text-xs">{schedule.successRate}%</span>
                          </div>
                        </div>
                        <div>
                          <span className="font-medium">Avg Execution</span>
                          <p className="text-muted-foreground">{schedule.avgExecutionTime}s</p>
                        </div>
                        <div>
                          <span className="font-medium">Last Run</span>
                          <p className="text-muted-foreground">
                            {schedule.lastRun ? new Date(schedule.lastRun).toLocaleString() : 'Never'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="triggers" className="flex-1 space-y-4 min-h-0">
            <div className="flex justify-between items-center">
              <div className="space-y-1">
                <h3 className="text-lg font-semibold">Event Triggers</h3>
                <p className="text-sm text-muted-foreground">Configure agents to respond to real-time events</p>
              </div>
              <Button onClick={() => setShowCreateTrigger()}>
                <Plus className="w-4 h-4 mr-2" />
                Create Trigger
              </Button>
            </div>

            <ScrollArea className="flex-1 min-h-0">
              <div className="space-y-4">
                {triggers.map((trigger) => (
                  <Card key={trigger.id}>
                    <CardHeader className="" hasCardAction={false}>
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            {getTriggerTypeIcon(trigger.type)}
                            <CardTitle className="text-base">{trigger.name}</CardTitle>
                            {getStatusIcon(trigger.enabled, trigger.successRate)}
                            <Badge variant="outline" className={getPriorityColor(trigger.priority)}>
                              {trigger.priority}
                            </Badge>
                            <Badge variant="secondary">
                              {trigger.type.replace('_', ' ')}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>{trigger.triggerCount} triggers</span>
                            <span>•</span>
                            <span>Avg response: {trigger.avgResponseTime}ms</span>
                            <span>•</span>
                            <span>Last: {trigger.lastTriggered ? new Date(trigger.lastTriggered).toLocaleString() : 'Never'}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline">
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline">
                            <Settings className="w-3 h-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {/* Toggle enabled state */}}
                          >
                            {trigger.enabled ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <h4 className="text-sm font-medium mb-1">Instructions</h4>
                        <p className="text-sm text-muted-foreground">{trigger.instructions}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium mb-1">Configuration</h4>
                        <div className="bg-gray-50 rounded p-2 text-xs font-mono">
                          {JSON.stringify(trigger.config, null, 2)}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Success Rate</span>
                          <div className="flex items-center gap-2 mt-1">
                            <Progress value={trigger.successRate} className="h-2 flex-1" />
                            <span className="text-xs">{trigger.successRate}%</span>
                          </div>
                        </div>
                        <div>
                          <span className="font-medium">Response Time</span>
                          <p className="text-muted-foreground">{trigger.avgResponseTime}ms</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="chains" className="flex-1 space-y-4 min-h-0">
            <div className="flex justify-between items-center">
              <div className="space-y-1">
                <h3 className="text-lg font-semibold">Trigger Chains</h3>
                <p className="text-sm text-muted-foreground">Create multi-agent workflows with sequential execution</p>
              </div>
              <Button onClick={() => setShowCreateChain()}>
                <Plus className="w-4 h-4 mr-2" />
                Create Chain
              </Button>
            </div>

            <ScrollArea className="flex-1 min-h-0">
              <div className="space-y-4">
                {chains.map((chain) => (
                  <Card key={chain.id}>
                    <CardHeader className="" hasCardAction={false}>
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <GitBranch className="w-4 h-4" />
                            <CardTitle className="text-base">{chain.name}</CardTitle>
                            {getStatusIcon(chain.enabled, chain.successRate)}
                            <Badge variant="outline">
                              {chain.stages.length} stages
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>{chain.executionCount} executions</span>
                            <span>•</span>
                            <span>Avg time: {chain.avgExecutionTime}s</span>
                            <span>•</span>
                            <span>Success: {chain.successRate}%</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline">
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline">
                            <Play className="w-3 h-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {/* Toggle enabled state */}}
                          >
                            {chain.enabled ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <h4 className="text-sm font-medium mb-2">Execution Stages</h4>
                        <div className="space-y-2">
                          {chain.stages.map((stage, index) => (
                            <div key={index} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-medium flex items-center justify-center">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-sm">{stage.agentName}</div>
                                <div className="text-xs text-muted-foreground">{stage.action}</div>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {stage.timeout}s timeout
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Success Rate</span>
                          <div className="flex items-center gap-2 mt-1">
                            <Progress value={chain.successRate} className="h-2 flex-1" />
                            <span className="text-xs">{chain.successRate}%</span>
                          </div>
                        </div>
                        <div>
                          <span className="font-medium">Avg Execution Time</span>
                          <p className="text-muted-foreground">{chain.avgExecutionTime}s</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="analytics" className="flex-1 space-y-4 min-h-0">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-3" hasCardAction={false}>
                    <CardTitle className="text-sm">Active Schedules</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{schedules.filter(s => s.enabled).length}</div>
                    <p className="text-xs text-muted-foreground">of {schedules.length} total</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-3" hasCardAction={false}>
                    <CardTitle className="text-sm">Active Triggers</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{triggers.filter(t => t.enabled).length}</div>
                    <p className="text-xs text-muted-foreground">of {triggers.length} total</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-3" hasCardAction={false}>
                    <CardTitle className="text-sm">Total Executions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {schedules.reduce((sum, s) => sum + s.runCount, 0) + triggers.reduce((sum, t) => sum + t.triggerCount, 0)}
                    </div>
                    <p className="text-xs text-muted-foreground">All time</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-3" hasCardAction={false}>
                    <CardTitle className="text-sm">Success Rate</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {(
                        (schedules.reduce((sum, s) => sum + s.successRate, 0) + 
                         triggers.reduce((sum, t) => sum + t.successRate, 0)) / 
                        (schedules.length + triggers.length)
                      ).toFixed(1)}%
                    </div>
                    <p className="text-xs text-muted-foreground">Average</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="" hasCardAction={false}>
                    <CardTitle className="text-base">Schedule Performance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-64">
                      <div className="space-y-3">
                        {schedules.map((schedule) => (
                          <div key={schedule.id} className="space-y-2">
                            <div className="flex justify-between items-start">
                              <span className="text-sm font-medium">{schedule.name}</span>
                              <Badge variant="outline">{schedule.runCount} runs</Badge>
                            </div>
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs text-muted-foreground">
                                <span>Success Rate</span>
                                <span>{schedule.successRate}%</span>
                              </div>
                              <Progress value={schedule.successRate} className="h-1" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="" hasCardAction={false}>
                    <CardTitle className="text-base">Trigger Activity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-64">
                      <div className="space-y-3">
                        {triggers.map((trigger) => (
                          <div key={trigger.id} className="space-y-2">
                            <div className="flex justify-between items-start">
                              <span className="text-sm font-medium">{trigger.name}</span>
                              <Badge variant="outline">{trigger.triggerCount} triggers</Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                              <div>Success: {trigger.successRate}%</div>
                              <div>Response: {trigger.avgResponseTime}ms</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Create Schedule Dialog */}
        {showCreateSchedule && (
          <Dialog open={showCreateSchedule} onOpenChange={setShowCreateSchedule}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Schedule</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="schedule-name">Schedule Name</Label>
                  <Input
                    id="schedule-name"
                    value={newSchedule.name}
                    onChange={(e) => setNewSchedule({...newSchedule, name: e.target.value})}
                    placeholder="e.g., Daily Security Scan"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="schedule-type">Schedule Type</Label>
                  <select
                    id="schedule-type"
                    value={newSchedule.type}
                    onChange={(e) => setNewSchedule({...newSchedule, type: e.target.value as any})}
                    className="w-full px-3 py-2 border rounded-md"
                    aria-label="Schedule type"
                  >
                    <option value="cron">Cron Expression</option>
                    <option value="interval">Fixed Interval</option>
                  </select>
                </div>

                {newSchedule.type === 'cron' ? (
                  <div className="space-y-2">
                    <Label htmlFor="cron-expression">Cron Expression</Label>
                    <Input
                      id="cron-expression"
                      value={newSchedule.cronExpression}
                      onChange={(e) => setNewSchedule({...newSchedule, cronExpression: e.target.value})}
                      placeholder="0 9 * * 1-5"
                    />
                    <p className="text-xs text-muted-foreground">
                      Examples: "0 9 * * 1-5" (weekdays at 9 AM), "0 */6 * * *" (every 6 hours)
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label htmlFor="interval">Interval</Label>
                      <Input
                        id="interval"
                        type="number"
                        value={newSchedule.interval}
                        onChange={(e) => setNewSchedule({...newSchedule, interval: parseInt(e.target.value)})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="interval-unit">Unit</Label>
                      <select
                        id="interval-unit"
                        value={newSchedule.intervalUnit}
                        onChange={(e) => setNewSchedule({...newSchedule, intervalUnit: e.target.value as any})}
                        className="w-full px-3 py-2 border rounded-md"
                        aria-label="Interval unit"
                      >
                        <option value="minutes">Minutes</option>
                        <option value="hours">Hours</option>
                        <option value="days">Days</option>
                      </select>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="schedule-action">Action</Label>
                  <Input
                    id="schedule-action"
                    value={newSchedule.action}
                    onChange={(e) => setNewSchedule({...newSchedule, action: e.target.value})}
                    placeholder="e.g., security_scan, backup_verify"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="schedule-instructions">Instructions</Label>
                  <Textarea
                    id="schedule-instructions"
                    value={newSchedule.instructions}
                    onChange={(e) => setNewSchedule({...newSchedule, instructions: e.target.value})}
                    placeholder="Detailed instructions for the agent..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="schedule-priority">Priority</Label>
                  <select
                    id="schedule-priority"
                    value={newSchedule.priority}
                    onChange={(e) => setNewSchedule({...newSchedule, priority: e.target.value as any})}
                    className="w-full px-3 py-2 border rounded-md"
                    aria-label="Schedule priority"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button onClick={handleCreateSchedule} className="flex-1">
                    Create Schedule
                  </Button>
                  <Button variant="outline" onClick={() => setShowCreateSchedule(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AgentSchedulingDialog;
